import {
  getOrganizationTreeSetupLabel,
  getServiceInstanceScopeLabel,
  isOrganizationServiceVariant,
} from '@/lib/service-variant-types'

export { getOrganizationTreeSetupLabel, getServiceInstanceScopeLabel } from '@/lib/service-variant-types'

export const SERVICE_INSTANCE_PARAM = 'service_instance_id'

/** Administration workspace for root org and tenant roles (replaces legacy `/org/tree`). */
export const ORG_ORGANIZATION_PAGE_PATH = '/org/organization'

/** Branch-operator workspace (org-branch variant); application-focused landing. */
export const ORG_BRANCH_WORKSPACE_PATH = '/org/branch'

/** Shared dashboard shell. The page renders the dashboard/home experience for the active variant. */
export const SERVICE_VARIANT_DASHBOARD_PATH = '/dashboard'

/** Service-variant entry point for non-org variants before resolving to their own dashboard/homepage. */
export const SERVICE_VARIANT_HOME_REDIRECT_PATH = '/services/dashboard'

/** Canonical path for org service instance settings form (`:id` is the tree node id). */
export const ORG_SERVICE_INSTANCE_FORM_BASE = '/org/service-instance/form'

/** Canonical base for listing instances under a tree node (`…/:id`). */
export const ORG_SERVICE_INSTANCE_LIST_BASE = '/org/service-instance'

export function buildOrgServiceInstanceFormPath(
  nodeId: string | number,
  query?: string | URLSearchParams,
): string {
  const base = `${ORG_SERVICE_INSTANCE_FORM_BASE}/${nodeId}`
  if (query == null) {
    return base
  }

  const search = typeof query === 'string'
    ? (query.startsWith('?') ? query.slice(1) : query)
    : query.toString()
  return search ? `${base}?${search}` : base
}

/** List of service instances under an org tree node (`…/:nodeId`). */
export function buildOrgServiceInstanceListPath(nodeId: string | number): string {
  return `${ORG_SERVICE_INSTANCE_LIST_BASE}/${nodeId}`
}

export function getServiceVariantHomePath(post?: PostServiceInstanceContext | null): string {
  if (!post || isOrganizationScopePost(post)) {
    return SERVICE_VARIANT_DASHBOARD_PATH
  }

  return SERVICE_VARIANT_HOME_REDIRECT_PATH
}

export function buildServiceVariantHomePath(
  post?: PostServiceInstanceContext | null,
  serviceInstanceId?: string | null,
): string {
  return withServiceInstanceId(getServiceVariantHomePath(post), serviceInstanceId)
}

type PostServiceInstanceContext = {
  id?: string | number | null
  service_instance?: string | number | null
  service_instance_id?: string | number | null
  service_variant_unique_id?: string | number | null
  service_variant_name?: string | null
  service_variant_type?: string | null
  capabilities?: Record<string, unknown> | null
  roles_data?: {
    id?: string | number | null
    roles_name?: string | null
    unique_id?: string | null
    permissions?: Record<string, unknown> | null
    role_level?: number | null
    role_level_code?: string | null
  } | null
}

type BranchServiceInstanceContext = {
  id?: string | number | null
  instanceId?: string | null
  code?: string | null
}

function normalizeServiceInstanceCandidate(value?: string | number | null): string | null {
  if (value === undefined || value === null) {
    return null
  }

  const normalizedValue = String(value).trim()
  return normalizedValue.length > 0 ? normalizedValue : null
}

function isLikelyPublicServiceInstanceId(value: string) {
  return /[A-Za-z_-]/.test(value)
}

function isOrganizationScopePost(post?: PostServiceInstanceContext | null) {
  return isOrganizationServiceVariant(
    post?.service_variant_unique_id,
    post?.service_variant_name ?? post?.service_variant_type,
  )
}

function isExactServiceInstanceMatch(
  post: PostServiceInstanceContext | null | undefined,
  serviceInstanceId: string,
) {
  return getServiceInstanceIdFromPost(post) === serviceInstanceId
}

function getServiceInstanceIdFromBranch(branch?: BranchServiceInstanceContext | null): string | null {
  return normalizeServiceInstanceCandidate(branch?.instanceId)
    || normalizeServiceInstanceCandidate(branch?.code)
    || normalizeServiceInstanceCandidate(branch?.id)
}

function isBranchMatch(branch: BranchServiceInstanceContext | null | undefined, serviceInstanceId: string) {
  const branchCandidates = [branch?.instanceId, branch?.code, branch?.id]
    .map((value) => normalizeServiceInstanceCandidate(value))
    .filter((value): value is string => Boolean(value))

  return branchCandidates.includes(serviceInstanceId)
}

function getPostRoleLevel(post?: PostServiceInstanceContext | null): number | null {
  const directLevel = post?.roles_data?.role_level
  if (typeof directLevel === 'number' && Number.isFinite(directLevel)) {
    return directLevel
  }

  const levelCode = String(post?.roles_data?.role_level_code ?? '').trim().toLowerCase()
  const match = levelCode.match(/role_level_(\d+)/)
  if (!match) {
    return null
  }

  const parsed = Number(match[1])
  return Number.isFinite(parsed) ? parsed : null
}

function getCapabilityCount(post?: PostServiceInstanceContext | null) {
  const capabilities = post?.capabilities
  if (!capabilities || typeof capabilities !== 'object') {
    return 0
  }

  return Object.values(capabilities).filter((value) => value === true).length
}

function getScopedPostPriority(post?: PostServiceInstanceContext | null) {
  const capabilityCount = getCapabilityCount(post)
  const roleLevel = getPostRoleLevel(post)
  const roleLevelBonus = roleLevel === null ? 0 : Math.max(0, 20 - roleLevel)

  return capabilityCount * 100 + roleLevelBonus
}

export function resolveScopedPostForServiceInstance<T extends PostServiceInstanceContext>({
  currentPost,
  availablePosts,
  serviceInstanceId,
  preferMostCapable: _preferMostCapable = false,
  includeOrganizationScopeFallback = false,
}: {
  currentPost?: T | null
  availablePosts?: T[] | null
  serviceInstanceId?: string | null
  preferMostCapable?: boolean
  includeOrganizationScopeFallback?: boolean
}): T | null {
  const targetServiceInstanceId = normalizeServiceInstanceCandidate(serviceInstanceId)
  const posts = [currentPost, ...(availablePosts ?? [])].filter((post): post is T => Boolean(post))

  if (!targetServiceInstanceId) {
    return currentPost ?? posts[0] ?? null
  }

  const exactMatchingPosts = posts.filter((post) => isExactServiceInstanceMatch(post, targetServiceInstanceId))
  const organizationFallbackPosts = includeOrganizationScopeFallback
    ? posts.filter((post) => (
      !isExactServiceInstanceMatch(post, targetServiceInstanceId)
      && isOrganizationScopePost(post)
    ))
    : []
  const candidatePosts = exactMatchingPosts.length > 0
    ? [...exactMatchingPosts, ...organizationFallbackPosts]
    : organizationFallbackPosts

  if (candidatePosts.length === 0) {
    return currentPost ?? posts[0] ?? null
  }

  const currentMatchesTarget = currentPost && isExactServiceInstanceMatch(currentPost, targetServiceInstanceId)
  // Preserve the actively selected post whenever it already matches the
  // requested service instance. Do not silently escalate to a more capable
  // role (for example root admin), because that feels like an automatic role
  // switch in the portal.
  if (currentMatchesTarget) {
    return currentPost
  }

  return candidatePosts
    .slice()
    .sort((left, right) => getScopedPostPriority(right) - getScopedPostPriority(left))[0] ?? null
}

function shouldUseCurrentBranchFallback(
  post: PostServiceInstanceContext | null | undefined,
  postServiceInstanceId: string | null,
  currentBranch: BranchServiceInstanceContext | null | undefined,
  availableBranches: BranchServiceInstanceContext[] | null | undefined,
) {
  if (!postServiceInstanceId || !currentBranch || isOrganizationScopePost(post)) {
    return false
  }

  const branches = availableBranches ?? []
  if (branches.length === 0) {
    return false
  }

  return !branches.some((branch) => isBranchMatch(branch, postServiceInstanceId))
}

/** Normalizes API values (e.g. "1", "2") to `local`, `s3`, or `not_configured`. */
export function normalizeServiceStorageType(
  value: string | null | undefined,
): 'local' | 's3' | 'not_configured' {
  const t = String(value ?? '')
    .toLowerCase()
    .trim()
  if (t === '2' || t === 's3') {
    return 's3'
  }
  if (t === '1' || t === 'local') {
    return 'local'
  }
  if (t === 'not_configured') {
    return 'not_configured'
  }
  return 'not_configured'
}

function resolveEffectiveServiceStorageType(
  instance: ServiceInstanceStorageShape,
): 'local' | 's3' | 'not_configured' {
  const ownMode = normalizeServiceStorageType(instance.storage_type)
  if (ownMode !== 'not_configured') {
    return ownMode
  }
  return normalizeServiceStorageType(instance.resolved_storage_type)
}

function resolveEffectiveServiceStoragePath(
  instance: ServiceInstanceStorageShape,
): string {
  const ownMode = normalizeServiceStorageType(instance.storage_type)
  if (ownMode !== 'not_configured') {
    return String(instance.static_file_path ?? '').trim()
  }
  return String(instance.resolved_static_file_path ?? instance.static_file_path ?? '').trim()
}

export type ServiceInstanceStorageShape = {
  storage_type?: string | null
  resolved_storage_type?: string | null
  static_file_path?: string | null
  resolved_static_file_path?: string | null
  storage_bucket_name?: string | null
  storage_access_key?: string | null
  storage_secret_key?: string | null
  storage_region_name?: string | null
  storage_ready?: boolean | null
}

/** Whether uploads can proceed given instance storage fields (aligned with backend FileUpload checks). */
export function getServiceInstanceStorageReadiness(
  instance: ServiceInstanceStorageShape | null | undefined,
): { ready: boolean; reason: string | null } {
  if (!instance) {
    return { ready: false, reason: 'Organization context could not be loaded.' }
  }

  const mode = resolveEffectiveServiceStorageType(instance)
  const path = resolveEffectiveServiceStoragePath(instance)
  if (mode === 'not_configured') {
    return { ready: false, reason: 'Configure storage on this branch or on a parent service instance.' }
  }
  if (mode === 'local') {
    if (!path) {
      return { ready: false, reason: 'Set a storage path under Configuration (Local storage).' }
    }
    if (!path.startsWith('/')) {
      return { ready: false, reason: 'Local storage path must be an absolute filesystem path.' }
    }
    return { ready: true, reason: null }
  }

  if (mode === 's3') {
    const hasCredentials = instance.storage_ready === true
      || Boolean(
        instance.storage_bucket_name
        && instance.storage_access_key
        && instance.storage_secret_key
        && instance.storage_region_name,
      )
    if (!path && hasCredentials) {
      return { ready: true, reason: null }
    }
    if (!path) {
      return { ready: false, reason: 'Set an HTTPS storage path under Configuration for S3 storage, or configure bucket credentials under Credentials.' }
    }
  }

  if (mode === 's3' && path) {
    try {
      const url = new URL(path)
      if (url.protocol !== 'https:') {
        return { ready: false, reason: 'S3 storage path must use HTTPS.' }
      }
    } catch {
      return { ready: false, reason: 'S3 storage path must be a valid HTTPS URL.' }
    }
  }

  // storage_ready is set by the backend when inherited credentials are fully configured
  // (access_key and secret_key are intentionally masked in the response for security)
  if (instance.storage_ready === true) {
    return { ready: true, reason: null }
  }
  if (instance.storage_ready === false) {
    return { ready: false, reason: 'Complete S3 bucket, region, access key, and secret under Credentials.' }
  }

  // Fallback: check visible fields if storage_ready is not set
  const bucket = String(instance.storage_bucket_name ?? '').trim()
  const access = String(instance.storage_access_key ?? '').trim()
  const secret = String(instance.storage_secret_key ?? '').trim()
  const region = String(instance.storage_region_name ?? '').trim()
  if (!bucket || !access || !secret || !region) {
    return { ready: false, reason: 'Complete S3 bucket, region, access key, and secret under Credentials.' }
  }
  return { ready: true, reason: null }
}

export function getServiceInstanceIdFromSearch(search: string): string | null {
  const value = new URLSearchParams(search).get(SERVICE_INSTANCE_PARAM)
  return value && value.trim() ? value : null
}

export function getServiceInstanceIdFromPost(post?: PostServiceInstanceContext | null): string | null {
  const directServiceInstanceId = normalizeServiceInstanceCandidate(post?.service_instance)
  if (directServiceInstanceId) {
    return directServiceInstanceId
  }

  const fallbackServiceInstanceId = normalizeServiceInstanceCandidate(post?.service_instance_id)
  if (fallbackServiceInstanceId && isLikelyPublicServiceInstanceId(fallbackServiceInstanceId)) {
    return fallbackServiceInstanceId
  }

  return null
}

export function resolveActiveServiceInstanceId({
  search,
  post,
  currentBranch,
  availableBranches,
  storedServiceInstanceId,
  token,
}: {
  search?: string
  post?: PostServiceInstanceContext | null
  currentBranch?: BranchServiceInstanceContext | null
  availableBranches?: BranchServiceInstanceContext[] | null
  storedServiceInstanceId?: string | null
  token?: string | null
}): string | null {
  const explicitServiceInstanceId = search ? getServiceInstanceIdFromSearch(search) : null
  const postServiceInstanceId = getServiceInstanceIdFromPost(post)
  const currentBranchServiceInstanceId = getServiceInstanceIdFromBranch(currentBranch)

  if (explicitServiceInstanceId) {
    return explicitServiceInstanceId
  }

  if (shouldUseCurrentBranchFallback(post, postServiceInstanceId, currentBranch, availableBranches)) {
    return currentBranchServiceInstanceId
      || postServiceInstanceId
      || storedServiceInstanceId
      || getServiceInstanceIdFromToken(token)
  }

  return postServiceInstanceId
    || currentBranchServiceInstanceId
    || storedServiceInstanceId
    || getServiceInstanceIdFromToken(token)
}

export function withServiceInstanceId(path: string, serviceInstanceId?: string | null): string {
  if (!serviceInstanceId) {
    return path
  }

  const [pathname, search = ''] = path.split('?')
  const params = new URLSearchParams(search)
  params.set(SERVICE_INSTANCE_PARAM, serviceInstanceId)

  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

/** Preserve workspace + branch-directory query params on links (list ↔ form ↔ media). */
export function withWorkspaceListContext(
  path: string,
  context: { serviceInstanceId?: string | null; uniqueLevelVariantId?: string | null },
): string {
  const merged = withServiceInstanceId(path, context.serviceInstanceId)
  const ulv = String(context.uniqueLevelVariantId ?? '').trim()
  if (!ulv) {
    return merged
  }

  const [pathname, search = ''] = merged.split('?')
  const params = new URLSearchParams(search)
  const genericLevelVariant = ulv.replace(/-lvl-variant$/i, '') || ulv
  params.set('lvl_variant', genericLevelVariant)
  params.set('unique_level_variant_id', ulv)
  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}

export function getServiceInstanceIdFromToken(token?: string | null): string | null {
  if (!token) {
    return null
  }

  try {
    const [, payload] = token.split('.')
    if (!payload) {
      return null
    }

    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/')
    const paddedPayload = normalizedPayload.padEnd(Math.ceil(normalizedPayload.length / 4) * 4, '=')
    const decodedPayload = JSON.parse(window.atob(paddedPayload)) as { instance_id?: string }

    return decodedPayload.instance_id || null
  } catch {
    return null
  }
}

type ServiceInstanceVariantSource = {
  service_variant?: string | number | null
  service_variant_name?: string | null
  service_data?: { variant_unique?: string | number | null } | null
  level_variant_name?: string | null
}

export function getServiceInstanceScopeLabelFromInstance(
  instance: ServiceInstanceVariantSource | null | undefined,
): string {
  return getServiceInstanceScopeLabel(
    instance?.service_data?.variant_unique ?? instance?.service_variant,
    instance?.service_variant_name,
    instance?.level_variant_name,
  )
}

export function getOrganizationTreeSetupLabelFromInstance(
  instance: ServiceInstanceVariantSource | null | undefined,
): string {
  return getOrganizationTreeSetupLabel(
    instance?.service_data?.variant_unique ?? instance?.service_variant,
    instance?.service_variant_name,
    instance?.level_variant_name,
  )
}
